import { type Panel } from "@shared/schema";
import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { getPanelImage } from "@/lib/images";

interface StoryPanelProps {
  panel: Panel;
  index: number;
}

export function StoryPanel({ panel, index }: StoryPanelProps) {
  const [leftImageLoaded, setLeftImageLoaded] = useState(false);
  const [rightImageLoaded, setRightImageLoaded] = useState(false);
  const [leftImageError, setLeftImageError] = useState(false);
  const [rightImageError, setRightImageError] = useState(false);

  return (
    <section
      className="relative md:min-h-screen flex flex-row"
      data-testid={`panel-${panel.number}`}
      aria-label={panel.title}
    >
      {/* Left Side - 90s Timeline */}
      <div
        className="relative w-1/2 md:min-h-screen flex items-center justify-center p-2 sm:p-4 md:p-12"
        style={{
          background: `hsl(var(--ganji-village-bg) / 0.65)`,
        }}
        data-testid={`panel-left-${panel.number}`}
      >
        <div className="relative max-w-2xl w-full">
          {/* Image */}
          <div className="relative aspect-[4/5] sm:aspect-[3/4] w-full mb-6 rounded-md overflow-hidden shadow-xl">
            {!leftImageLoaded && !leftImageError && (
              <Skeleton className="absolute inset-0 w-full h-full" />
            )}
            {leftImageError ? (
              <div
                className="absolute inset-0 flex items-center justify-center bg-muted p-8 text-center"
                style={{ fontFamily: "'Architects Daughter', cursive" }}
              >
                <div>
                  <p className="text-lg text-muted-foreground mb-2">
                    [Image missing]
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {panel.leftSide.scene_description}
                  </p>
                </div>
              </div>
            ) : (
              <img
                src={getPanelImage(panel.number, "left")}
                alt={panel.leftSide.alt_text}
                className={`w-full h-full object-cover transition-opacity duration-500 ${leftImageLoaded ? "opacity-100" : "opacity-0"
                  }`}
                onLoad={() => setLeftImageLoaded(true)}
                onError={() => {
                  setLeftImageError(true);
                  setLeftImageLoaded(true);
                }}
                loading="lazy"
                data-testid={`img-left-${panel.number}`}
              />
            )}
          </div>

          {/* Caption */}
          <div
            className="text-center px-4"
            style={{ fontFamily: "'Architects Daughter', cursive" }}
          >
            <p
              className="text-sm sm:text-base md:text-2xl leading-relaxed whitespace-normal break-words text-ramadan-mixed font-bold"
              data-testid={`caption-left-${panel.number}`}
            >
              {panel.leftSide.caption}
            </p>
          </div>

          {/* Removed Optional Labels rendering */}

        </div>
      </div>

      {/* Center Gradient Divider */}
      <div
        className="hidden md:block absolute left-1/2 top-0 bottom-0 w-20 -translate-x-1/2 pointer-events-none z-10"
        style={{
          background:
            "linear-gradient(to right, hsl(var(--ganji-village-bg) / 0.65), hsl(var(--ganji-urban-bg) / 0.65))",
        }}
        aria-hidden="true"
      />

      {/* Right Side - Gen Z Timeline */}
      <div
        className="relative w-1/2 md:min-h-screen flex items-center justify-center p-2 sm:p-4 md:p-12"
        style={{
          background: `hsl(var(--ganji-urban-bg) / 0.65)`,
        }}
        data-testid={`panel-right-${panel.number}`}
      >
        <div className="relative max-w-2xl w-full">
          {/* Image */}
          <div className="relative aspect-[4/5] sm:aspect-[3/4] w-full mb-6 rounded-md overflow-hidden shadow-xl">
            {!rightImageLoaded && !rightImageError && (
              <Skeleton className="absolute inset-0 w-full h-full" />
            )}
            {rightImageError ? (
              <div
                className="absolute inset-0 flex items-center justify-center bg-muted p-8 text-center"
                style={{ fontFamily: "'Architects Daughter', cursive" }}
              >
                <div>
                  <p className="text-lg text-muted-foreground mb-2">
                    [Image missing]
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {panel.rightSide.scene_description}
                  </p>
                </div>
              </div>
            ) : (
              <img
                src={getPanelImage(panel.number, "right")}
                alt={panel.rightSide.alt_text}
                className={`w-full h-full object-cover transition-opacity duration-500 ${rightImageLoaded ? "opacity-100" : "opacity-0"
                  }`}
                onLoad={() => setRightImageLoaded(true)}
                onError={() => {
                  setRightImageError(true);
                  setRightImageLoaded(true);
                }}
                loading="lazy"
                data-testid={`img-right-${panel.number}`}
              />
            )}
          </div>

          {/* Caption */}
          <div
            className="text-center px-4"
            style={{ fontFamily: "'Architects Daughter', cursive" }}
          >
            <p
              className="text-sm sm:text-base md:text-2xl leading-relaxed whitespace-normal break-words holi-text-mixed font-bold"
              data-testid={`caption-right-${panel.number}`}
            >
              {panel.rightSide.caption}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
